import { useEffect, useState } from "react";
import { Plus, Trash2, Wallet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Field, Loading, EmptyState, Stat } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { money, fmtDay, todayKey } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const CATS = ["supplies", "fuel", "equipment", "insurance", "marketing", "software", "wages", "other"];

function ExpenseDialog({ open, onOpenChange, onSaved }) {
  const { tz } = useAuth();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setF({ date: todayKey(tz), amount: "", category: "supplies", vendor: "", note: "" }); setError(""); }
  }, [open, tz]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await api.post("/expenses", { ...f, amount: Number(f.amount) }); toast.success("Expense recorded"); onSaved(); onOpenChange(false); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="expense-dialog">
        <DialogHeader><DialogTitle className="font-display">Record expense</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date" htmlFor="x-date"><Input id="x-date" type="date" required value={f.date || ""} onChange={(e) => set("date", e.target.value)} className="h-11" data-testid="expense-date-input" /></Field>
            <Field label="Amount ($)" htmlFor="x-amt"><Input id="x-amt" type="number" min="0.01" step="0.01" required value={f.amount ?? ""} onChange={(e) => set("amount", e.target.value)} className="h-11" data-testid="expense-amount-input" /></Field>
            <Field label="Category"><Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger className="h-11" data-testid="expense-category-select"><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Vendor" htmlFor="x-v"><Input id="x-v" value={f.vendor || ""} onChange={(e) => set("vendor", e.target.value)} className="h-11" data-testid="expense-vendor-input" /></Field>
          </div>
          <Field label="Note" htmlFor="x-n"><Input id="x-n" value={f.note || ""} onChange={(e) => set("note", e.target.value)} className="h-11" data-testid="expense-note-input" /></Field>
          {error && <p className="text-sm text-red-700" role="alert" data-testid="expense-error">{error}</p>}
          <DialogFooter><Button type="submit" disabled={busy} data-testid="expense-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save expense</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Expenses() {
  const { tz } = useAuth();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/expenses").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    try { await api.delete(`/expenses/${id}`); toast.success("Expense deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const month = todayKey(tz).slice(0, 7);
  const monthTotal = (list || []).filter((x) => x.date.startsWith(month)).reduce((s, x) => s + x.amount, 0);

  return (
    <div data-testid="expenses-page">
      <PageHeader title="Expenses" subtitle="Business costs used in your profit estimate." actions={<Button className="h-11" onClick={() => setOpen(true)} data-testid="new-expense-btn"><Plus className="h-4 w-4 mr-1" />Record expense</Button>} />
      <div className="grid sm:grid-cols-3 gap-4 mb-6"><Stat label="This month" value={money(monthTotal)} icon={Wallet} testId="expenses-month-total" /></div>
      {!list ? <Loading /> : list.length === 0 ? <EmptyState icon={Wallet} title="No expenses recorded" testId="expenses-empty" /> : (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="expenses-list">
          {list.map((x) => (
            <div key={x.id} className="flex items-center gap-4 px-5 py-3" data-testid={`expense-row-${x.id}`}>
              <div className="flex-1 min-w-0"><div className="font-medium text-slate-900 capitalize">{x.vendor || x.category}</div><div className="text-xs text-slate-500 capitalize">{fmtDay(x.date)} · {x.category}{x.note ? ` · ${x.note}` : ""}</div></div>
              <div className="font-semibold text-slate-900">{money(x.amount)}</div>
              <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => remove(x.id)} aria-label="Delete expense" data-testid={`delete-expense-${x.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
      <ExpenseDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
